/**
 * MECE Numbering Utilities
 *
 * Centralized numbering functions for consistent pillar/tactic identification
 * across all components: MECE Table, Strategy Map, Analysis Report, Playbook Export
 */

import type { MECEPillar, MECETactic } from '@models/index'

/**
 * Get pillar display number (e.g., "1.", "2.")
 * Uses priority_rank if available, falls back to index
 */
export function getPillarNumber(pillar: MECEPillar, pillarIndex: number): string {
  const num = pillar.priority_rank ?? (pillarIndex + 1)
  return `${num}.`
}

/**
 * Get tactic display number (e.g., "1.1.", "2.3.")
 * Uses priority_rank + priority if available, falls back to indices
 */
export function getTacticNumber(
  pillar: MECEPillar,
  pillarIndex: number,
  tactic: MECETactic,
  tacticIndex: number
): string {
  const pillarNum = pillar.priority_rank ?? (pillarIndex + 1)
  const tacticNum = tactic.priority ?? (tacticIndex + 1)
  return `${pillarNum}.${tacticNum}.`
}

/**
 * Get pillar number as integer (for sorting/comparison)
 */
export function getPillarNumberInt(pillar: MECEPillar, pillarIndex: number): number {
  return pillar.priority_rank ?? (pillarIndex + 1)
}

/**
 * Get tactic reference ID for cross-component linking
 * Format: "P{pillarNum}T{tacticNum}" (e.g., "P1T2")
 */
export function getTacticReferenceId(
  pillar: MECEPillar,
  pillarIndex: number,
  tactic: MECETactic,
  tacticIndex: number
): string {
  const pillarNum = pillar.priority_rank ?? (pillarIndex + 1)
  const tacticNum = tactic.priority ?? (tacticIndex + 1)
  return `P${pillarNum}T${tacticNum}`
}

/**
 * Parse a tactic reference ID back to pillar/tactic numbers
 * Input: "P1T2" -> { pillarNum: 1, tacticNum: 2 }
 */
export function parseTacticReferenceId(refId: string): { pillarNum: number; tacticNum: number } | null {
  const match = refId.match(/^P(\d+)T(\d+)$/)
  if (!match) return null
  return {
    pillarNum: parseInt(match[1], 10),
    tacticNum: parseInt(match[2], 10)
  }
}

/**
 * Find a tactic by its reference ID within a MECE table
 * Returns { pillar, pillarIndex, tactic, tacticIndex } or null
 */
export function findTacticByReference(
  pillars: MECEPillar[],
  refId: string
): { pillar: MECEPillar; pillarIndex: number; tactic: MECETactic; tacticIndex: number } | null {
  const parsed = parseTacticReferenceId(refId)
  if (!parsed) return null

  for (let pIdx = 0; pIdx < pillars.length; pIdx++) {
    const pillar = pillars[pIdx]
    const pillarNum = pillar.priority_rank ?? (pIdx + 1)
    if (pillarNum !== parsed.pillarNum) continue

    for (let tIdx = 0; tIdx < pillar.tactics.length; tIdx++) {
      const tactic = pillar.tactics[tIdx]
      const tacticNum = tactic.priority ?? (tIdx + 1)
      if (tacticNum === parsed.tacticNum) {
        return { pillar, pillarIndex: pIdx, tactic, tacticIndex: tIdx }
      }
    }
  }
  return null
}
